import React from "react";
import { Typography, Button, Grid, Box } from "@material-ui/core";
import pretty from "Shared/pretty";
import { useFormContext } from "react-hook-form";
import { CreateDisbursementFormFields } from "../Hooks/useCreateDisbursementForm";
import { formatPaymentAmount } from "../OffCyclePaymentFormContainer";
import { DPSpacer } from "Components/DP";
import useStyles from "./styles";
import { useEmployeeProfileQuery } from "Hooks/useEmployeeProfileQueryContext";
import { usePartnerConfig } from "Hooks/usePartnerConfig";

type OffCyclePaymentFormConfirmationDialogProps = {
  displayDuplicateWarning: boolean;
  displayTerminationWarning: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

const OffCyclePaymentFormConfirmationDialog = ({
  displayDuplicateWarning,
  displayTerminationWarning,
  onCancel,
  onSubmit,
}: OffCyclePaymentFormConfirmationDialogProps) => {
  const classes = useStyles();
  const { userDetails } = useEmployeeProfileQuery();
  const { offCyclePaymentOptions } = usePartnerConfig();
  const { getValues } = useFormContext<CreateDisbursementFormFields>();

  if (!userDetails) {
    return null;
  }

  const { firstName, lastName, termsAccepted } = userDetails.employee;

  const paymentString = pretty.dollar(formatPaymentAmount(getValues()));

  let message = `Send ${paymentString} to ${firstName} ${lastName}? You will be invoiced for the amount as well as the applicable fee.`;
  if (!termsAccepted) {
    message = message + " The user will receive an automatic confirmation email.";
  }

  const secondaryApprovalMessage = "This payment must be approved by a colleague before it is sent.";

  const terminationMessage = "Payments sent for Termination to a DailyPay user's DPA will also cancel their DailyPay account.";

  const duplicateMessage = `This payment appears to be a duplicate. A payment of ${paymentString} has already been issued in the last 24 hours. Are you sure you want to proceed?`;

  return (
    <Box display="flex" flexDirection="column" padding={4}>
      <Typography
        align="center"
        id="OffCyclePaymentFormConfirmationDialogMessage"
      >
        {message}
      </Typography>
      {offCyclePaymentOptions.cycleRequiresApproval && (
        <>
          <DPSpacer spacing={2} />
          <Typography align="center">
            {secondaryApprovalMessage}
          </Typography>
        </>
      )}
      {displayTerminationWarning && (
        <>
          <DPSpacer spacing={2} />
          <Typography align="center">
            {terminationMessage}
          </Typography>
        </>
      )}
      <DPSpacer spacing={3} />
      {displayDuplicateWarning && (
        <Typography
          id="DuplicateDisbursementWarning"
          align="center"
          color={"error"}
          className={classes.duplicateMessage}
        >
          {duplicateMessage}
        </Typography>
      )}
      <DPSpacer spacing={2} />
      <Grid container justify="center" spacing={2}>
        <Grid item>
          <Button
            color="primary"
            size="large"
            onClick={onSubmit}
            variant="contained"
          >
            OK
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="default"
            onClick={onCancel}
            size="large"
            variant="contained"
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OffCyclePaymentFormConfirmationDialog;
